import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import firmsService, { Firm, FirmCreateInput, FirmUpdateInput } from '../../services/firmsService';
import { StatisticsResponse, RoleStatistics } from '../../types/statistics';

interface FirmsState {
  firms: Firm[];
  currentFirm: Firm | null;
  statistics: RoleStatistics | null;
  statisticsResponse: StatisticsResponse | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: FirmsState = {
  firms: [],
  currentFirm: null,
  statistics: null,
  statisticsResponse: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
};

// Async thunks
export const fetchFirms = createAsyncThunk(
  'firms/fetchFirms',
  async (params: { page?: number; limit?: number; filters?: Record<string, any> } = {}) => {
    const response = await firmsService.getFirms(params.page, params.limit, params.filters);
    return response;
  }
);

export const fetchFirmById = createAsyncThunk(
  'firms/fetchFirmById',
  async (firmId: number) => {
    const response = await firmsService.getFirmById(firmId);
    return response;
  }
);

export const createFirm = createAsyncThunk(
  'firms/createFirm',
  async (firmData: FirmCreateInput, { rejectWithValue }) => {
    try {
      const response = await firmsService.createFirm(firmData);
      return response;
    } catch (error: any) {
      // Carry the backend's `{ message, errors }` body through as the
      // rejected payload (instead of RTK's default serialized-error, which
      // drops it) so callers' `.unwrap()` catch can read field-level
      // validation errors from a 422.
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const updateFirm = createAsyncThunk(
  'firms/updateFirm',
  async ({ firmId, firmData }: { firmId: number; firmData: FirmUpdateInput }, { rejectWithValue }) => {
    try {
      const response = await firmsService.updateFirm(firmId, firmData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const deleteFirm = createAsyncThunk(
  'firms/deleteFirm',
  async (firmId: number, { rejectWithValue }) => {
    try {
      await firmsService.deleteFirm(firmId);
      return firmId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const fetchFirmStatistics = createAsyncThunk(
  'firms/fetchStatistics',
  async (firmId: number) => {
    const response = await firmsService.getFirmStatistics(firmId);
    return response;
  }
);

export const fetchGlobalStatistics = createAsyncThunk(
  'firms/fetchGlobalStatistics',
  async () => {
    const response = await firmsService.getGlobalStatistics();
    return response;
  }
);

export const toggleFirmStatus = createAsyncThunk(
  'firms/toggleStatus',
  async (
    { firmId, status }: { firmId: number; status: 'active' | 'inactive' | 'suspended' },
    { rejectWithValue },
  ) => {
    try {
      const response = await firmsService.toggleFirmStatus(firmId, status);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const uploadFirmLogo = createAsyncThunk(
  'firms/uploadLogo',
  async ({ firmId, file }: { firmId: number; file: File }) => {
    const response = await firmsService.uploadLogo(firmId, file);
    return response;
  }
);

const firmsSlice = createSlice({
  name: 'firms',
  initialState,
  reducers: {
    clearCurrentFirm: (state) => {
      state.currentFirm = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    updateFirmInList: (state, action) => {
      const index = state.firms.findIndex(f => f.id === action.payload.id);
      if (index !== -1) {
        state.firms[index] = action.payload;
      }
      if (state.currentFirm?.id === action.payload.id) {
        state.currentFirm = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch firms
      .addCase(fetchFirms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFirms.fulfilled, (state, action) => {
        state.loading = false;
        state.firms = action.payload.data || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchFirms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch firms';
      })
      
      // Fetch firm by ID
      .addCase(fetchFirmById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFirmById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFirm = action.payload;
      })
      .addCase(fetchFirmById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch firm';
      })
      
      // Create firm
      .addCase(createFirm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFirm.fulfilled, (state, action) => {
        state.loading = false;
        state.firms.unshift(action.payload);
      })
      .addCase(createFirm.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to create firm';
      })
      
      // Update firm
      .addCase(updateFirm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFirm.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.firms.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.firms[index] = action.payload;
        }
        if (state.currentFirm?.id === action.payload.id) {
          state.currentFirm = action.payload;
        }
      })
      .addCase(updateFirm.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to update firm';
      })
      
      // Delete firm
      .addCase(deleteFirm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFirm.fulfilled, (state, action) => {
        state.loading = false;
        state.firms = state.firms.filter(f => f.id !== action.payload);
        if (state.currentFirm?.id === action.payload) {
          state.currentFirm = null;
        }
      })
      .addCase(deleteFirm.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to delete firm';
      })
      
      // Fetch statistics
      .addCase(fetchFirmStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFirmStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statisticsResponse = action.payload;
        state.statistics = action.payload.data;
      })
      .addCase(fetchFirmStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch firm statistics';
      })
      
      // Fetch global statistics
      .addCase(fetchGlobalStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statisticsResponse = action.payload;
        state.statistics = action.payload.data;
      })
      .addCase(fetchGlobalStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch global statistics';
      })
      
      // Toggle status
      .addCase(toggleFirmStatus.fulfilled, (state, action) => {
        const index = state.firms.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.firms[index] = action.payload;
        }
        if (state.currentFirm?.id === action.payload.id) {
          state.currentFirm = action.payload;
        }
      })
      
      // Upload logo
      .addCase(uploadFirmLogo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadFirmLogo.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.firms.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.firms[index] = action.payload;
        }
        if (state.currentFirm?.id === action.payload.id) {
          state.currentFirm = action.payload;
        }
      })
      .addCase(uploadFirmLogo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to upload logo';
      });
  },
});

export const { clearCurrentFirm, clearError, setPagination, updateFirmInList } = firmsSlice.actions;

export default firmsSlice.reducer;
